import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  getProfiles,
  getCatalog,
  getWorkers,
  putProfile,
  putProfileImage,
} from '@/api/adapter/onboarding';
import { adapterConfig } from '@/api/config';
import { useAuthStore } from '@/stores/authStore';
import { validateImage, weeklyMinutes } from '@/lib/auth-validation';
import {
  completeWorkerCommand,
  pendingWorkerCommand,
} from '@/services/workerRegistration';
import type {
  ClientProfile,
  WorkerProfile,
  Qualifications,
  WorkerSummary,
} from '@/types/session';

export interface WorkerProfileEdits {
  qualifications: Qualifications;
  hours: string;
}

export const profileKeys = {
  mine: (principal: string) => ['current-profiles', principal] as const,
  worker: (principal: string) => ['current-worker', principal] as const,
};
export function profileImageUrl(
  principal: string,
  section: 'client' | 'worker',
  revision = 0
) {
  return `${adapterConfig.baseURL}/profiles/${encodeURIComponent(principal)}/${section}/image?v=${revision}`;
}
export function useMyProfiles() {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: profileKeys.mine(principal ?? ''),
    queryFn: async () => {
      const profiles = await getProfiles();
      if (profiles.principalId !== principal)
        throw new Error('Your account changed. Reload before editing.');
      useAuthStore.getState().setProfiles(profiles);
      return profiles;
    },
    enabled: !!principal,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
export function useMyWorker() {
  const session = useAuthStore((state) => state.session);
  return useQuery({
    queryKey: profileKeys.worker(session?.principalId ?? ''),
    queryFn: async () =>
      (await getWorkers()).find(
        (worker) => worker.account === session?.accountId
      ) ?? null,
    enabled: !!session,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
export function useSaveProfile(section: 'client' | 'worker') {
  const queryClient = useQueryClient();
  const principal = useAuthStore((state) => state.session?.principalId);
  const completed = useRef({
    principal,
    profile: '',
    image: null as File | null,
  });
  const controller = useRef<AbortController>();
  useEffect(() => () => controller.current?.abort(), []);
  const mutation = useMutation({
    mutationFn: async ({
      profile,
      image,
      worker: edits,
      onStatus = () => {},
    }: {
      profile: ClientProfile | WorkerProfile;
      image: File | null;
      worker?: WorkerProfileEdits;
      onStatus?: (message: string) => void;
    }) => {
      if (!principal)
        throw new Error('Your account changed. Reload before saving.');
      if (controller.current)
        throw new Error('A profile save is already running.');
      const current = new AbortController();
      controller.current = current;
      const assertOwner = () => {
        if (
          !principal ||
          current.signal.aborted ||
          useAuthStore.getState().session?.principalId !== principal
        )
          throw new Error('Your account changed. Reload before continuing.');
      };
      let stage = 'Profile';
      try {
        assertOwner();
        if (completed.current.principal !== principal)
          completed.current = { principal, profile: '', image: null };
        if (image) validateImage(image);
        const minutes = edits
          ? weeklyMinutes(edits.hours.trim() === '' ? NaN : Number(edits.hours))
          : null;
        const readWorker = async () => {
          const account = useAuthStore.getState().session?.accountId;
          const latest = (await getWorkers()).find(
            (entry) => entry.account === account
          );
          assertOwner();
          if (!latest)
            throw new Error(
              'Worker details are unavailable. Reload before saving.'
            );
          queryClient.setQueryData(profileKeys.worker(principal), latest);
          return latest;
        };
        let latest: WorkerSummary | undefined;
        if (edits) {
          const catalog = await getCatalog();
          assertOwner();
          if (
            edits.qualifications.roleIds.some(
              (id) => !catalog.roles.some((role) => role.id === id)
            ) ||
            edits.qualifications.skillIds.some(
              (id) => !catalog.skills.some((skill) => skill.id === id)
            )
          )
            throw new Error(
              'An unavailable qualification is selected. Review the catalog before saving.'
            );
          latest = await readWorker();
          // Validate saved requests before writing profile data; never discard uncertain commands.
          pendingWorkerCommand(principal, 'qualifications');
          pendingWorkerCommand(principal, 'calendar');
        }
        const profileValue = JSON.stringify(profile);
        if (completed.current.profile !== profileValue) {
          onStatus('Saving profile…');
          const profiles =
            section === 'client'
              ? await putProfile('client', profile as ClientProfile)
              : await putProfile('worker', profile as WorkerProfile);
          assertOwner();
          completed.current.profile = profileValue;
          queryClient.setQueryData(profileKeys.mine(principal), profiles);
          useAuthStore.getState().setProfiles(profiles);
        }
        stage = 'Image';
        if (image && completed.current.image !== image) {
          onStatus('Saving profile photo…');
          await putProfileImage(section, image);
          assertOwner();
          completed.current.image = image;
        }
        if (edits && latest && minutes !== null) {
          stage = 'Qualifications';
          const pendingQualifications = pendingWorkerCommand(
            principal,
            'qualifications'
          );
          if (pendingQualifications) {
            const done = await completeWorkerCommand(
              principal,
              'qualifications',
              pendingQualifications.payload,
              onStatus,
              current.signal
            );
            assertOwner();
            if (!done)
              throw new Error(
                'Qualifications are still pending. Save again to check the same request.'
              );
            latest = {
              ...latest,
              qualifications: pendingQualifications.payload.qualifications,
            };
            queryClient.setQueryData(profileKeys.worker(principal), latest);
            latest = await readWorker();
          }
          if (
            JSON.stringify(latest.qualifications) !==
            JSON.stringify(edits.qualifications)
          ) {
            onStatus('Saving roles and skills…');
            const done = await completeWorkerCommand(
              principal,
              'qualifications',
              { qualifications: edits.qualifications },
              onStatus,
              current.signal
            );
            assertOwner();
            if (!done)
              throw new Error(
                'Qualifications are still pending. Save again to check the same request.'
              );
            latest = { ...latest, qualifications: edits.qualifications };
            queryClient.setQueryData(profileKeys.worker(principal), latest);
            latest = await readWorker();
          }
          stage = 'Availability';
          const pendingCalendar = pendingWorkerCommand(principal, 'calendar');
          if (pendingCalendar) {
            const done = await completeWorkerCommand(
              principal,
              'calendar',
              pendingCalendar.payload,
              onStatus,
              current.signal
            );
            assertOwner();
            if (!done)
              throw new Error(
                'Availability is still pending. Save again to check the same request.'
              );
            latest = {
              ...latest,
              calendar: {
                ...latest.calendar,
                definition: pendingCalendar.payload,
              },
            };
            queryClient.setQueryData(profileKeys.worker(principal), latest);
          }
          // Preserve the latest overrides and let the backend check reservations.
          latest = await readWorker();
          if (latest.calendar.definition.defaultWeeklyMinutes !== minutes) {
            onStatus('Saving weekly availability…');
            const calendar = {
              ...latest.calendar.definition,
              defaultWeeklyMinutes: minutes,
            };
            const done = await completeWorkerCommand(
              principal,
              'calendar',
              calendar,
              onStatus,
              current.signal
            );
            assertOwner();
            if (!done)
              throw new Error(
                'Availability is still pending. Save again to check the same request.'
              );
            latest = {
              ...latest,
              calendar: { ...latest.calendar, definition: calendar },
            };
            queryClient.setQueryData(profileKeys.worker(principal), latest);
            await readWorker();
          }
          assertOwner();
        }
        onStatus('');
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Please retry.';
        throw new Error(
          completed.current.profile && stage !== 'Profile'
            ? `Some changes are already saved. ${stage}: ${message} Retry Save to finish; confirmed writes will not be repeated.`
            : message
        );
      } finally {
        if (controller.current === current) controller.current = undefined;
      }
    },
  });
  return {
    ...mutation,
    reset: () => {
      completed.current = { principal, profile: '', image: null };
      mutation.reset();
    },
  };
}
